import { useController } from "react-hook-form";

import TextAreaInput from "@/form/inputs/TextAreaInput/TextAreaInput";
import { useFieldIds } from "@/form/useFieldIds";
import { cn } from "@/lib/utils";

import FieldError from "../FieldError/FieldError";
import FieldHint from "../FieldHint/FieldHint";
import FieldLabel from "../FieldLabel/FieldLabel";

export type TextAreaFieldProps = {
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly autoFocus?: boolean;
  readonly label: string;
  readonly labelClassName?: string;
  readonly hint?: string;
  readonly placeholder?: string;
  readonly name: string;
  readonly "data-testid"?: string;
  readonly onBlur?: () => void;
  readonly onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  readonly onKeyUp?: (value: string) => void;
  readonly onChange?: (value: string) => void;
  readonly hideLabel?: boolean;
  readonly hideValidation?: boolean;
};

export default function TextAreaField({
  disabled = false,
  label,
  name,
  hint,
  placeholder,
  onBlur,
  onFocus,
  onChange,
  hideLabel = false,
  hideValidation = false,
}: TextAreaFieldProps): React.ReactElement {
  const { inputId, labelId, hintId, errorId, describedBy } = useFieldIds(name);

  const { field, fieldState } = useController({
    name,
  });

  const invalid = !!fieldState.error;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e.target.value);
    field.onChange(e.target.value);
  };

  const handleBlur = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onBlur?.();
    field.onBlur();
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    onFocus?.(e);
  };

  return (
    <div
      className={cn("TextAreaField grid gap-2")}
      data-testid="TextAreaField"
      data-field={name}
    >
      <div className={cn("grid", hideLabel && "sr-only")}>
        <FieldLabel
          htmlFor={inputId}
          id={labelId}
          invalid={invalid}
          value={label}
        />
      </div>

      <TextAreaInput
        id={inputId}
        aria-describedby={describedBy(!!hint, !!fieldState.error)}
        aria-invalid={invalid}
        disabled={disabled}
        name={name}
        placeholder={placeholder}
        value={field.value || ""}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        ref={field.ref}
        autoFocus={false}
      />

      <div
        className={cn(
          "align-center body1  flex justify-end",
          hideValidation && "hidden"
        )}
      >
        {hint && !fieldState.error && <FieldHint hint={hint} id={hintId} />}
        {fieldState.error && (
          <FieldError error={fieldState.error} id={errorId} />
        )}
      </div>
    </div>
  );
}
